// src/pages/ClientAdminSubOrgs.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Building2, Calendar, ChevronRight, Loader2 } from "lucide-react";

interface SubOrg {
  id: string;
  name: string;
  created_at: string;
  archived: boolean;
}

export default function ClientAdminSubOrgs() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string>("Your Client");
  const [subOrgs, setSubOrgs] = useState<SubOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSubOrgName, setNewSubOrgName] = useState("");
  const [creating, setCreating] = useState(false);

  // Helper to detect RLS/permission errors
  const isRlsBlock = (err: any) =>
    err?.code === "42501" ||
    err?.status === 403 ||
    (typeof err?.message === "string" && err.message.toLowerCase().includes("row-level security"));

  // Load client ID via RPC and sub-orgs
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        // 1) Get client ID via RPC
        const { data: fetchedClientId, error: clientErr } = await supabase.rpc("get_my_client_id");
        console.log("[ClientAdminSubOrgs] get_my_client_id:", { clientId: fetchedClientId, clientErr });

        if (!active) return;

        if (clientErr) {
          setError(clientErr.message);
          setLoading(false);
          return;
        }

        if (!fetchedClientId) {
          setError("You are not linked to a client account.");
          setLoading(false);
          return;
        }

        setClientId(fetchedClientId);

        // Fetch client name via RPC first, fallback to direct query
        const { data: rpcData, error: rpcErr } = await (supabase as any).rpc("admin_get_client_name", { p_client_id: fetchedClientId });
        if (!rpcErr && rpcData?.[0]?.name) {
          setClientName(rpcData[0].name);
        } else {
          // Fallback to direct query for non-admin
          const { data: clientData, error: clientNameErr } = await supabase
            .from("clients")
            .select("name")
            .eq("id", fetchedClientId)
            .single();
          
          if (!clientNameErr && clientData?.name) {
            setClientName(clientData.name);
          } else if (isRlsBlock(clientNameErr)) {
            console.log("[ClientAdminSubOrgs] clients REST blocked, client name will use default");
          }
        }

        // 2) Fetch sub-organizations - try REST first
        const { data, error: fetchError } = await supabase
          .from("sub_organizations")
          .select("id, name, created_at, archived")
          .eq("client_id", fetchedClientId)
          .eq("archived", false)
          .order("created_at", { ascending: false });

        if (!fetchError) {
          if (!active) return;
          setSubOrgs(data ?? []);
        } else if (isRlsBlock(fetchError)) {
          // Fallback to RPC for Test environment (if RPC exists)
          console.log("[ClientAdminSubOrgs] sub_organizations REST blocked, falling back to RPC");
          try {
            const { data: rpcData, error: rpcErr } = await (supabase as any).rpc("client_admin_list_sub_orgs");
            
            if (rpcErr) {
              console.warn("[ClientAdminSubOrgs] client_admin_list_sub_orgs RPC failed:", rpcErr);
              throw rpcErr;
            }

            if (!active) return;
            // Map RPC response to expected format
            const rpcArray = Array.isArray(rpcData) ? rpcData : [];
            const mappedSubOrgs = rpcArray.map((r: any) => ({
              id: r.sub_org_id,
              name: r.sub_org_name,
              created_at: r.created_at,
              archived: r.archived,
            }));
            setSubOrgs(mappedSubOrgs);
            if (rpcArray[0]?.client_name) {
              setClientName(rpcArray[0].client_name);
            }
          } catch (rpcFallbackErr: any) {
            // RPC doesn't exist or failed - surface the original REST error
            console.warn("[ClientAdminSubOrgs] RPC fallback failed, using original error");
            throw fetchError;
          }
        } else {
          throw fetchError;
        }
      } catch (e: any) {
        if (!active) return;
        console.error("[ClientAdminSubOrgs] error:", e);
        setError(e?.message || "Failed to load sub-organizations");
      } finally {
        if (!active) return;
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const handleCreateSubOrg = async () => {
    if (!newSubOrgName.trim()) {
      toast({
        title: "Validation error",
        description: "Sub-organization name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!clientId) {
      toast({
        title: "Error",
        description: "Client context not available.",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      const { data, error: insertError } = await supabase
        .from("sub_organizations")
        .insert({
          name: newSubOrgName.trim(),
          client_id: clientId,
        })
        .select("id, name, created_at, archived")
        .single();

      if (insertError) throw insertError;

      // Add to local state
      setSubOrgs((prev) => [data, ...prev]);
      setNewSubOrgName("");
      setIsModalOpen(false);

      toast({
        title: "Success",
        description: `Sub-organization "${data.name}" created successfully.`,
      });
    } catch (e: any) {
      console.error("[ClientAdminSubOrgs] create error:", e);
      toast({
        title: "Error",
        description: e?.message || "Failed to create sub-organization.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (loading) {
    return (
      <AppLayout title="Sub-Organizations — Loading">
        <div className="min-h-screen flex items-center justify-center">
          <div className="space-y-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">
              Loading sub-organizations…
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Error state
  if (error || !clientId) {
    return (
      <AppLayout title="Sub-Organizations">
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Building2 className="h-5 w-5" />
                Access Issue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {error || "Unable to load sub-organizations."}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/client/sub-orgs")}
              >
                Back to Sub-Organizations
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={`${clientName} — Sub-Organizations`}>
      <div className="min-h-screen bg-background px-4 py-6 md:px-8 md:py-8">
        {/* Client Name Header */}
        <div className="max-w-5xl mx-auto mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
            {clientName}
          </h1>
        </div>

        {/* Sub-header */}
        <header className="max-w-5xl mx-auto mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
              Sub-Organizations
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage sub-organizations under your client account
            </p>
          </div>

          <Button size="sm" onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Create Sub-Organization
          </Button>
        </header>

        {/* Sub-orgs list */}
        <main className="max-w-5xl mx-auto">
          {subOrgs.length === 0 ? (
            <Card className="p-8 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">No sub-organizations yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first sub-organization to start organizing portfolios.
              </p>
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Create Sub-Organization
              </Button>
            </Card>
          ) : (
            <div className="grid gap-3">
              {subOrgs.map((subOrg) => (
                <Card
                  key={subOrg.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/client/sub-orgs/${subOrg.id}/portfolios`)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{subOrg.name}</h3>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          Created {formatDate(subOrg.created_at)}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        {/* Create Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Sub-Organization</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sub-org-name">Name *</Label>
                <Input
                  id="sub-org-name"
                  placeholder="Enter sub-organization name"
                  value={newSubOrgName}
                  onChange={(e) => setNewSubOrgName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !creating) {
                      handleCreateSubOrg();
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateSubOrg} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
